import React from 'react';
import { RE2JS } from 're2js';

import { useMemoizedValue } from '@neptune/shared/common-util';

import { isNotUndefined } from 'common/tsHelpers';

import { getGroupsValuesFor } from './secondary-styles-patterns';

function sectionStyles() {
  let cache = new Map<string, Map<string, number>>();

  return {
    read(sectionId: string) {
      return cache.get(sectionId) ?? new Map<string, number>();
    },
    override(incoming: Map<string, Map<string, number>>) {
      cache = incoming;
    },
  };
}

function freeSlots() {
  const cache = new Map<string, Set<number>>();

  return (sectionId: string) => {
    let freeSlots = cache.get(sectionId);

    if (!freeSlots) {
      freeSlots = new Set<number>();
      cache.set(sectionId, freeSlots);
    }

    return freeSlots;
  };
}

const EMPTY_MAP = new Map<string, Map<string, number>>();

export function useSecondaryStylesMapping(
  patterns: string[] | undefined,
  sectionIdToAttributesMap: Map<string, Set<string>>,
) {
  const memoizedPatterns = useMemoizedValue<string[] | undefined>(patterns);

  const getAttributeLabels = React.useMemo(() => {
    if (!memoizedPatterns) {
      return () => undefined;
    }

    const cache = new Map<string, string | undefined>();
    const patterns = memoizedPatterns
      .map((pattern) => {
        try {
          return RE2JS.compile(pattern);
        } catch {
          return undefined;
        }
      })
      .filter(isNotUndefined);

    return (attribute: string) => {
      if (cache.has(attribute)) {
        return cache.get(attribute);
      }

      const key = getKeyForAttribute(attribute, patterns);

      cache.set(attribute, key);

      return key;
    };
  }, [memoizedPatterns]);

  const parseAttributes = React.useCallback(
    (attributes: Set<string>) => {
      const labeledAttributes = new Set<string>();
      const otherAttributes = new Set<string>();
      const labels = new Set<string>();

      for (const attribute of attributes) {
        const label = getAttributeLabels(attribute);

        if (label) {
          labeledAttributes.add(attribute);
          labels.add(label);
        } else {
          otherAttributes.add(attribute);
        }
      }

      return [labeledAttributes, otherAttributes, labels];
    },
    [getAttributeLabels],
  );

  const prevSectionIdToAttributeStylesRef =
    React.useRef<ReturnType<typeof sectionStyles>>(sectionStyles());
  const prevSectionIdToLabelStylesRef =
    React.useRef<ReturnType<typeof sectionStyles>>(sectionStyles());
  const sectionFreeLabelSlotsRef = React.useRef<ReturnType<typeof freeSlots>>(freeSlots());
  const sectionFreeAttributeSlotsRef = React.useRef<ReturnType<typeof freeSlots>>(freeSlots());

  const [sectionIdToLabelStylesMap, sectionIdToAttributeStylesMap] = React.useMemo(() => {
    if (!memoizedPatterns?.length) {
      return [EMPTY_MAP, EMPTY_MAP];
    }

    const labelsResult = new Map<string, Map<string, number>>();
    const attributesResult = new Map<string, Map<string, number>>();

    for (const [sectionId, attributes] of sectionIdToAttributesMap) {
      const [labeledAttributes, otherAttributes, labels] = parseAttributes(attributes);

      const freeLabelSlots = sectionFreeLabelSlotsRef.current(sectionId);
      const freeAttributeSlots = sectionFreeAttributeSlotsRef.current(sectionId);
      const previousLabelStylesMap = prevSectionIdToLabelStylesRef.current.read(sectionId);
      const previousAttributeStylesMap = prevSectionIdToAttributeStylesRef.current.read(sectionId);

      for (const [key, value] of previousLabelStylesMap) {
        if (!labels.has(key)) {
          freeLabelSlots.add(value);
        }
      }

      for (const [key, value] of previousAttributeStylesMap) {
        if (!otherAttributes.has(key)) {
          freeAttributeSlots.add(value);
        }
      }

      const attributeStylesMap = new Map<string, number>();
      const labelStylesMap = new Map<string, number>();
      let last = -1;

      const applyLabeledStyle = (attribute: string, label: string, value: number) => {
        let isValueUsed = false;

        if (!attributeStylesMap.has(attribute)) {
          isValueUsed = true;
          attributeStylesMap.set(attribute, value);
        }

        if (!labelStylesMap.has(label)) {
          isValueUsed = true;
          labelStylesMap.set(label, value);
        }

        if (isValueUsed) {
          last = Math.max(last, value);
          freeLabelSlots.delete(value);
          freeAttributeSlots.delete(value);
        }
      };

      const addedAttributes = new Set<string>();

      for (const attribute of labeledAttributes) {
        const label = getAttributeLabels(attribute);

        if (!label) {
          continue;
        }

        const value = labelStylesMap.get(label) ?? previousLabelStylesMap.get(label);

        if (value === undefined) {
          addedAttributes.add(attribute);
        } else {
          applyLabeledStyle(attribute, label, value);
        }
      }

      for (const attribute of addedAttributes) {
        const label = getAttributeLabels(attribute);

        if (!label) {
          continue;
        }

        let value = labelStylesMap.get(label) ?? freeLabelSlots.values().next().value;

        if (value === undefined) {
          value = labelStylesMap.size;
        }

        applyLabeledStyle(attribute, label, value);
      }

      labelsResult.set(sectionId, labelStylesMap);

      let otherAttributeIndex = last + 1;
      const conflictingAttributes = new Set<string>();

      const nextUnlabeledValue = (): number => {
        let value = freeAttributeSlots.values().next().value;

        if (value !== undefined) {
          freeAttributeSlots.delete(value);
        } else {
          value = otherAttributeIndex;
        }

        return value;
      };

      for (const attribute of otherAttributes) {
        if (!attributeStylesMap.has(attribute)) {
          let value = previousAttributeStylesMap.get(attribute);

          if (value !== undefined && value <= last) {
            conflictingAttributes.add(attribute);
          } else {
            if (value === undefined) {
              value = nextUnlabeledValue();
            }

            otherAttributeIndex = Math.max(otherAttributeIndex, value + 1);
            attributeStylesMap.set(attribute, value);
          }
        }
      }

      for (const attribute of conflictingAttributes) {
        if (!attributeStylesMap.has(attribute)) {
          const value = nextUnlabeledValue();

          otherAttributeIndex++;

          attributeStylesMap.set(attribute, value);
        }
      }

      attributesResult.set(sectionId, attributeStylesMap);
    }

    return [labelsResult, attributesResult];
  }, [sectionIdToAttributesMap, getAttributeLabels, parseAttributes, memoizedPatterns?.length]);

  prevSectionIdToAttributeStylesRef.current.override(sectionIdToAttributeStylesMap);
  prevSectionIdToLabelStylesRef.current.override(sectionIdToLabelStylesMap);

  return sectionIdToAttributeStylesMap;
}

export const getKeyForAttribute = (attribute: string, patterns: RE2JS[]): string | undefined => {
  const entries = Object.entries(getGroupsValuesFor(patterns, attribute));

  if (entries.length === 0) {
    return undefined;
  }

  return JSON.stringify(Object.fromEntries(entries.sort()));
};
